import { useEffect, useState } from "react";

import {
  ActionIcon,
  Badge,
  Button,
  CopyButton,
  Group,
  Loader,
  Modal,
  rem,
  Select,
  Switch,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconBook,
  IconCheck,
  IconCopy,
  IconPhoneCall,
  IconSearch,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { DateTimePicker, TextInput } from "components";
import ConfirmationModal from "components/modal/confirmation";
import { AppConstants, RouteConstants } from "constant";
import { CallScriptsTrackingService, DashboardService } from "services";
import {
  DashboardAgentOptionResponse,
  DashboardCallOutcomeRequest,
  DashboardCallWidgetClientResponse,
  DashboardCallWidgetSearchResultResponse,
  UserResponse,
} from "types";
import { AgentStatusUtils, StorageUtils } from "utils";

import { CallScriptsModal, ScriptEndType, ScriptModuleId } from "./call-scripts";

import classes from "./CallWidgetSection.module.css";

type CallAction = "success" | "failure" | "resolved" | null;
type SuccessResult = "requestPreparation" | "notInterested" | "scheduledFollowUp" | null;
type FailureResult = "notReachedCombox" | "combox" | "notReached" | "busy" | null;
type WidgetStatus = "loading" | "ready" | "empty" | "error";
type ToggleableStatusField = "hasCreditCard" | "hasLeasing" | "planningCredit" | "hasCredit";

const formatTimeValue = (value: string | null) => value ?? "–";

// countryCode has been observed both with ("+381") and without ("41") a leading "+" - avoid
// double-prefixing.
const formatPhoneNumber = (countryCode: string, phoneNumber: string) =>
  `${countryCode.startsWith("+") ? countryCode : `+${countryCode}`} ${phoneNumber}`;

// Confirmed against the real backend enum (DashboardCallOutcomeResult in types.d.ts).
const SUCCESS_RESULT_CODE: Record<Exclude<SuccessResult, null>, DashboardCallOutcomeRequest["result"]> = {
  notInterested: "NOT_INTERESTED",
  scheduledFollowUp: "RESCHEDULED",
  requestPreparation: "PREPARE_REQUEST",
};

const FAILURE_RESULT_CODE: Record<Exclude<FailureResult, null>, DashboardCallOutcomeRequest["result"]> = {
  notReached: "NICHT_ERREICHT",
  combox: "COMBOX",
  notReachedCombox: "NICHT_ERREICHT_COMBOX",
  busy: "BESETZT",
};

type IncomingOutcome = "forwarded" | "taken" | "interrupted" | null;

// "type" only has 3 confirmed values, and the confirmed "result" enum (7 values) is fully
// accounted for by the outbound success/failure options - none are left for incoming outcomes,
// so this flow sends "type" alone. "forwarded" and "interrupted" are indistinguishable from each
// other on the backend as a result (both UNSUCCESSFUL, no result) - flag this to backend if the
// distinction matters; for now this is the best mapping available.
const INCOMING_OUTCOME_TYPE_CODE: Record<Exclude<IncomingOutcome, null>, DashboardCallOutcomeRequest["type"]> = {
  forwarded: "UNSUCCESSFUL",
  taken: "RESOLVED_WITHOUT_CALL",
  interrupted: "UNSUCCESSFUL",
};
const INCOMING_WIDGET_SOURCE: DashboardCallOutcomeRequest["widgetSource"] = "EMPTY";

const getAgentActivityBadge = (status: string, t: (key: string) => string) => {
  if (AgentStatusUtils.isBreakStatus(status)) {
    return { color: "yellow", label: t("agentStatus.onBreak") };
  }

  if (status === "INACTIVE") {
    return { color: "gray", label: t("agentStatus.inactive") };
  }

  return { color: "green", label: t("agentStatus.active") };
};

interface TimeAvailabilityProps {
  from: string;
  label: string;
  to: string;
  t: (key: string) => string;
}

const TimeAvailability = ({ from, label, to, t }: TimeAvailabilityProps) => {
  return (
    <div className={classes.infoBlock}>
      <Text className={classes.infoTitle}>{label}</Text>

      <div className={classes.timeRow}>
        <Text className={classes.timeLabel}>{t("dashboard.call.fields.from")}</Text>
        <div className={classes.timeValue}>{from}</div>
        <Text className={classes.timeLabel}>{t("dashboard.call.fields.to")}</Text>
        <div className={classes.timeValue}>{to}</div>
      </div>
    </div>
  );
};

interface OutcomeOptionProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

const OutcomeOption = ({ active, label, onClick }: OutcomeOptionProps) => {
  return (
    <button className={classes.outcomeOption} onClick={onClick} type="button">
      <span className={`${classes.outcomeRadio} ${active ? classes.outcomeRadioActive : ""}`}>
        {active ? <span className={classes.outcomeRadioDot} /> : null}
      </span>
      <span>{label}</span>
    </button>
  );
};

interface ActionButtonProps {
  active: boolean;
  ariaLabel: string;
  children?: React.ReactNode;
  onClick: () => void;
  tone: "success" | "failure" | "resolved";
}

const ActionButton = ({ active, ariaLabel, children, onClick, tone }: ActionButtonProps) => {
  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={active}
      className={`${classes.actionButton} ${classes[`actionButton${tone.charAt(0).toUpperCase()}${tone.slice(1)}`]} ${
        active ? classes.actionButtonActive : ""
      }`}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
};

const CallWidgetSection = () => {
  const { t } = useTranslation();
  const [selectedAction, setSelectedAction] = useState<CallAction>(null);
  const [selectedSuccessResult, setSelectedSuccessResult] = useState<SuccessResult>(null);
  const [selectedFailureResult, setSelectedFailureResult] = useState<FailureResult>(null);
  const [scriptsOpen, setScriptsOpen] = useState(false);
  const [usedScript, setUsedScript] = useState<{
    moduleId: ScriptModuleId;
    endType: ScriptEndType;
  } | null>(null);
  const [client, setClient] = useState<DashboardCallWidgetClientResponse | null>(null);
  const [widgetStatus, setWidgetStatus] = useState<WidgetStatus>("loading");
  const [scheduledCallAt, setScheduledCallAt] = useState<Date | null>(null);
  const [scheduledCallNote, setScheduledCallNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState<string | null>(null);
  const [blacklistModalOpen, setBlacklistModalOpen] = useState(false);
  const [blacklistReasonDraft, setBlacklistReasonDraft] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchStatus, setSearchStatus] = useState<"idle" | "loading" | "results" | "empty" | "error">(
    "idle",
  );
  const [searchResults, setSearchResults] = useState<DashboardCallWidgetSearchResultResponse[]>([]);
  const [selectedSearchResult, setSelectedSearchResult] =
    useState<DashboardCallWidgetSearchResultResponse | null>(null);
  const [incomingOutcome, setIncomingOutcome] = useState<IncomingOutcome>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleAgentOptions, setScheduleAgentOptions] = useState<DashboardAgentOptionResponse[]>([]);
  const [scheduleAgentOptionsLoading, setScheduleAgentOptionsLoading] = useState(false);
  const [scheduleAgentId, setScheduleAgentId] = useState<string | null>(null);
  const [scheduleDueDate, setScheduleDueDate] = useState<Date | null>(null);
  const [scheduleNote, setScheduleNote] = useState("");
  const [isScheduling, setIsScheduling] = useState(false);
  const [cancelAppointmentModalOpen, setCancelAppointmentModalOpen] = useState(false);
  const [isCancelingAppointment, setIsCancelingAppointment] = useState(false);

  const user = StorageUtils.getObject(AppConstants.STORAGE_USER) as UserResponse | undefined;
  const agentName = user?.fullName ?? "";
  const clientName = client ? `${client.firstName} ${client.lastName}` : "";
  const phoneNumber = client ? formatPhoneNumber(client.countryCode, client.phoneNumber) : "";

  const isSearchMode = searchQuery.trim().length > 0;
  const agentActivityBadge = selectedSearchResult
    ? getAgentActivityBadge(selectedSearchResult.agentActivityStatus, t)
    : null;
  const hasActionSelection = selectedAction !== null;
  const canSave =
    selectedAction === "resolved" ||
    (selectedAction === "success" &&
      selectedSuccessResult !== null &&
      (selectedSuccessResult !== "scheduledFollowUp" || scheduledCallAt !== null)) ||
    (selectedAction === "failure" && selectedFailureResult !== null);
  const successOptions = [
    {
      value: "requestPreparation" as const,
      label: t("dashboard.call.outcomes.success.options.requestPreparation"),
    },
    {
      value: "notInterested" as const,
      label: t("dashboard.call.outcomes.success.options.notInterested"),
    },
    {
      value: "scheduledFollowUp" as const,
      label: t("dashboard.call.outcomes.success.options.scheduledFollowUp"),
    },
  ];
  const failureOptions = [
    {
      value: "notReachedCombox" as const,
      label: t("dashboard.call.outcomes.failure.options.notReachedCombox"),
    },
    {
      value: "combox" as const,
      label: t("dashboard.call.outcomes.failure.options.combox"),
    },
    {
      value: "notReached" as const,
      label: t("dashboard.call.outcomes.failure.options.notReached"),
    },
    {
      value: "busy" as const,
      label: t("dashboard.call.outcomes.failure.options.busy"),
    },
  ];
  const incomingOutcomeOptions = [
    {
      value: "forwarded" as const,
      label: t("dashboard.call.incoming.outcomes.options.forwarded"),
    },
    {
      value: "taken" as const,
      label: t("dashboard.call.incoming.outcomes.options.taken"),
    },
    {
      value: "interrupted" as const,
      label: t("dashboard.call.incoming.outcomes.options.interrupted"),
    },
  ];

  useEffect(() => {
    let isMounted = true;
    setWidgetStatus("loading");

    DashboardService.getCallWidgetNextClient()
      .then((data) => {
        if (!isMounted) {
          return;
        }

        if (!data?.clientId) {
          setClient(null);
          setWidgetStatus("empty");
          return;
        }

        setClient(data);
        setWidgetStatus("ready");
      })
      .catch(() => {
        if (isMounted) {
          setClient(null);
          setWidgetStatus("error");
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const digitsOnly = searchQuery.replace(/\D/g, "");

    // Avoid broad searches while allowing Swiss local and international phone formats.
    if (digitsOnly.length < 5) {
      setSearchStatus("idle");
      setSearchResults([]);
      setSelectedSearchResult(null);
      setIncomingOutcome(null);
      setCancelAppointmentModalOpen(false);
      return;
    }

    setSearchStatus("loading");
    setSearchResults([]);
    setSelectedSearchResult(null);
    setIncomingOutcome(null);
    setCancelAppointmentModalOpen(false);

    let isCurrentQuery = true;
    const timeout = setTimeout(() => {
      DashboardService.searchCallWidgetClients({ phone: digitsOnly })
        .then((data) => {
          if (!isCurrentQuery) {
            return;
          }

          setSearchResults(data);
          setSelectedSearchResult(data.length === 1 ? data[0] : null);
          setSearchStatus(data.length > 0 ? "results" : "empty");
        })
        .catch(() => {
          if (isCurrentQuery) {
            setSearchStatus("error");
          }
        });
    }, 400);

    return () => {
      isCurrentQuery = false;
      clearTimeout(timeout);
    };
  }, [searchQuery]);

  const handleIncomingOutcomeSave = async () => {
    if (!selectedSearchResult || !incomingOutcome || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      const nextClient = await DashboardService.submitCallOutcome(selectedSearchResult.clientId, {
        type: INCOMING_OUTCOME_TYPE_CODE[incomingOutcome],
        widgetSource: INCOMING_WIDGET_SOURCE,
      });

      setIncomingOutcome(null);
      // Clearing the query also resets the search view back to idle via the effect above.
      setSearchQuery("");

      if (!nextClient?.clientId) {
        setClient(null);
        setWidgetStatus("empty");
      } else {
        setClient(nextClient);
        setWidgetStatus("ready");
      }
    } catch (err) {
      notifications.show({
        color: "red",
        title: t("dashboard.call.notifications.saveError.title"),
        message: t("dashboard.call.notifications.saveError.message"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenScheduleModal = () => {
    setScheduleAgentId(null);
    setScheduleDueDate(null);
    setScheduleNote("");
    setScheduleModalOpen(true);
    setScheduleAgentOptionsLoading(true);

    DashboardService.getAgentOptions()
      .then((options) => setScheduleAgentOptions(options.filter((agent) => agent.id !== user?.id)))
      .catch(() => setScheduleAgentOptions([]))
      .finally(() => setScheduleAgentOptionsLoading(false));
  };

  const handleScheduleConfirm = async () => {
    if (!selectedSearchResult || !scheduleAgentId || !scheduleDueDate || isScheduling) {
      return;
    }

    setIsScheduling(true);

    try {
      await DashboardService.scheduleCallForAgent({
        targetAgentId: Number(scheduleAgentId),
        clientId: selectedSearchResult.clientId,
        dueDate: scheduleDueDate.toISOString(),
        note: scheduleNote || undefined,
      });

      notifications.show({
        color: "green",
        title: t("dashboard.call.scheduleModal.successTitle"),
        message: t("dashboard.call.scheduleModal.successMessage"),
      });
      setScheduleModalOpen(false);
    } catch (err) {
      notifications.show({
        color: "red",
        title: t("dashboard.call.scheduleModal.errorTitle"),
        message: t("dashboard.call.scheduleModal.errorMessage"),
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const handleCancelAppointmentConfirm = async () => {
    if (!selectedSearchResult?.todoId || isCancelingAppointment) {
      return;
    }

    setIsCancelingAppointment(true);

    try {
      await DashboardService.cancelScheduledTodo(selectedSearchResult.todoId);

      notifications.show({
        color: "green",
        title: t("dashboard.call.cancelAppointmentModal.successTitle"),
        message: t("dashboard.call.cancelAppointmentModal.successMessage"),
      });

      setSelectedSearchResult((prev) => (prev ? { ...prev, todoId: null } : prev));
      setCancelAppointmentModalOpen(false);
    } catch (err) {
      notifications.show({
        color: "red",
        title: t("dashboard.call.cancelAppointmentModal.errorTitle"),
        message: t("dashboard.call.cancelAppointmentModal.errorMessage"),
      });
    } finally {
      setIsCancelingAppointment(false);
    }
  };

  const handleActionChange = (action: Exclude<CallAction, null>) => {
    if (action === selectedAction) {
      return;
    }

    setSelectedAction(action);
    setBlacklistReason(null);

    if (action !== "success") {
      setSelectedSuccessResult(null);
      setScheduledCallAt(null);
      setScheduledCallNote("");
    }

    if (action !== "failure") {
      setSelectedFailureResult(null);
    }
  };

  const handleBlacklistButtonClick = () => {
    if (blacklistReason !== null) {
      setBlacklistReason(null);
      return;
    }

    setBlacklistReasonDraft("");
    setBlacklistModalOpen(true);
  };

  const handleBlacklistConfirm = () => {
    const trimmedReason = blacklistReasonDraft.trim();

    if (!trimmedReason) {
      return;
    }

    setBlacklistReason(trimmedReason);
    setBlacklistModalOpen(false);
  };

  const handleSuccessResultChange = (result: Exclude<SuccessResult, null>) => {
    setSelectedSuccessResult(result);

    if (result !== "scheduledFollowUp") {
      setScheduledCallAt(null);
      setScheduledCallNote("");
    }
  };

  const handleToggleStatus = async (field: ToggleableStatusField) => {
    if (!client) {
      return;
    }

    const nextValue = !client[field];

    try {
      await DashboardService.toggleCallWidgetClientStatus(client.clientId, {
        name: field,
        value: nextValue,
      });
      // Update locally instead of refetching, to avoid flickering the widget.
      setClient((prev) => (prev ? { ...prev, [field]: nextValue } : prev));
    } catch (err) {
      notifications.show({
        color: "red",
        title: t("dashboard.call.notifications.toggleError.title"),
        message: t("dashboard.call.notifications.toggleError.message"),
      });
    }
  };

  const handleSave = async () => {
    if (!client || !selectedAction || !canSave || isSaving) {
      return;
    }

    const outcomeType: DashboardCallOutcomeRequest["type"] =
      selectedAction === "success"
        ? "SUCCESSFUL"
        : selectedAction === "failure"
          ? "UNSUCCESSFUL"
          : "RESOLVED_WITHOUT_CALL";

    const payload: DashboardCallOutcomeRequest = {
      type: outcomeType,
      widgetSource: client.source,
    };

    if (selectedAction === "success" && selectedSuccessResult) {
      payload.result = SUCCESS_RESULT_CODE[selectedSuccessResult];

      if (selectedSuccessResult === "scheduledFollowUp" && scheduledCallAt) {
        payload.scheduledCallAt = scheduledCallAt.toISOString();

        if (scheduledCallNote) {
          payload.scheduledCallNote = scheduledCallNote;
        }
      }
    }

    if (selectedAction === "failure" && selectedFailureResult) {
      payload.result = FAILURE_RESULT_CODE[selectedFailureResult];
    }

    if (blacklistReason) {
      payload.blacklist = true;
      payload.blacklistReason = blacklistReason;
    }

    if (usedScript?.moduleId) {
      payload.script = usedScript.moduleId;
    }

    setIsSaving(true);

    try {
      const nextClient = await DashboardService.submitCallOutcome(client.clientId, payload);

      // Linked to the call script the agent used, if any - kept as separate local analytics
      // from the persisted call outcome above.
      CallScriptsTrackingService.trackCallScriptUsage({
        moduleId: usedScript?.moduleId ?? null,
        endType: usedScript?.endType ?? null,
        clientId: client.clientId,
        clientName,
        agentId: user?.id ?? null,
        agentName,
        callAction: selectedAction,
        successResult: selectedSuccessResult,
        failureResult: selectedFailureResult,
        occurredAt: new Date().toISOString(),
      });

      setSelectedAction(null);
      setSelectedSuccessResult(null);
      setSelectedFailureResult(null);
      setScheduledCallAt(null);
      setScheduledCallNote("");
      setBlacklistReason(null);
      setUsedScript(null);

      if (!nextClient?.clientId) {
        setClient(null);
        setWidgetStatus("empty");
      } else {
        setClient(nextClient);
        setWidgetStatus("ready");
      }
    } catch (err) {
      notifications.show({
        color: "red",
        title: t("dashboard.call.notifications.saveError.title"),
        message: t("dashboard.call.notifications.saveError.message"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={classes.root}>
      <TextInput
        className={classes.search}
        leftSection={<IconSearch size={20} stroke={1.9} />}
        leftSectionPointerEvents="none"
        onChange={(event) => setSearchQuery(event.currentTarget.value)}
        placeholder={t("dashboard.call.searchPlaceholder")}
        radius="xl"
        rightSection={
          searchQuery ? (
            <ActionIcon
              aria-label={t("dashboard.call.search.clear")}
              onClick={() => setSearchQuery("")}
              radius="xl"
              variant="subtle"
            >
              <IconX size={16} />
            </ActionIcon>
          ) : null
        }
        size="md"
        styles={{
          input: {
            height: rem(42),
            borderColor: "var(--mantine-color-gray-4)",
            color: "var(--mantine-color-gray-7)",
            fontSize: rem(15),
            fontWeight: 600,
            paddingLeft: rem(46),
          },
          section: {
            color: "var(--mantine-color-gray-5)",
            width: rem(42),
          },
        }}
        value={searchQuery}
      />

      <div className={classes.content}>
        {isSearchMode ? (
          <>
            {searchStatus === "idle" ? (
              <Text className={classes.stateMessage}>{t("dashboard.call.search.hint")}</Text>
            ) : null}

            {searchStatus === "loading" ? (
              <div className={classes.stateMessage}>
                <Loader size="sm" />
              </div>
            ) : null}

            {searchStatus === "error" ? (
              <Text className={classes.stateMessage}>{t("dashboard.call.search.error")}</Text>
            ) : null}

            {searchStatus === "empty" ? (
              <Text className={classes.stateMessage}>{t("dashboard.call.search.empty")}</Text>
            ) : null}

            {searchStatus === "results" && !selectedSearchResult ? (
              <div className={classes.searchResultsList}>
                {searchResults.map((result) => (
                  <button
                    className={classes.searchResultRow}
                    key={result.clientId}
                    onClick={() => {
                      setSelectedSearchResult(result);
                      setIncomingOutcome(null);
                    }}
                    type="button"
                  >
                    <Text className={classes.searchResultName}>{`${result.firstName} ${result.lastName}`}</Text>
                    <Text className={classes.searchResultPhone}>
                      {formatPhoneNumber(result.countryCode, result.phoneNumber)}
                    </Text>
                  </button>
                ))}
              </div>
            ) : null}

            {searchStatus === "results" && selectedSearchResult ? (
              <>
                {searchResults.length > 1 ? (
                  <button
                    className={classes.searchBackLink}
                    onClick={() => {
                      setSelectedSearchResult(null);
                      setIncomingOutcome(null);
                    }}
                    type="button"
                  >
                    {t("dashboard.call.search.backToResults")}
                  </button>
                ) : null}

                <div className={classes.header}>
                  <div className={classes.headerMain}>
                    <div className={classes.clientHeadingRow}>
                      <Text className={classes.clientHeading}>
                        {`${selectedSearchResult.firstName} ${selectedSearchResult.lastName}  ${formatPhoneNumber(
                          selectedSearchResult.countryCode,
                          selectedSearchResult.phoneNumber,
                        )}`}
                      </Text>

                      <CopyButton
                        timeout={1200}
                        value={formatPhoneNumber(selectedSearchResult.countryCode, selectedSearchResult.phoneNumber)}
                      >
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? t("dashboard.call.actions.copied") : t("dashboard.call.actions.copy")}
                            withArrow
                          >
                            <ActionIcon
                              aria-label={t("dashboard.call.actions.copy")}
                              className={classes.copyButton}
                              onClick={copy}
                              radius="xl"
                              variant="subtle"
                            >
                              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </div>

                    <Text className={classes.sourceTitle}>
                      {t(`source.options.${selectedSearchResult.clientSource}`, {
                        defaultValue: selectedSearchResult.clientSource,
                      })}
                    </Text>
                  </div>

                  <RouterLink
                    aria-label={t("dashboard.call.actions.openClient")}
                    className={classes.profileLink}
                    to={`${RouteConstants.ROUTE_CLIENTS}/${selectedSearchResult.clientId}`}
                  >
                    <IconUser size={20} stroke={1.9} />
                  </RouterLink>
                </div>

                <div className={classes.searchAgentRow}>
                  <Text className={classes.searchAgentLabel}>{t("dashboard.call.search.assignedAgent")}</Text>
                  <Text className={classes.searchAgentName}>{selectedSearchResult.agentName}</Text>
                  <Badge color={agentActivityBadge?.color}>{agentActivityBadge?.label}</Badge>
                </div>

                <div className={classes.statusBar}>
                  <Badge color={selectedSearchResult.hasCredit ? "primary" : "gray"} variant="light">
                    {t("dashboard.call.status.hasCredit")}
                  </Badge>
                  <Badge color={selectedSearchResult.hasCreditCard ? "primary" : "gray"} variant="light">
                    {t("dashboard.call.status.hasCreditCard")}
                  </Badge>
                  <Badge color={selectedSearchResult.hasLeasing ? "primary" : "gray"} variant="light">
                    {t("dashboard.call.status.hasLeasing")}
                  </Badge>
                  <Badge color={selectedSearchResult.planningCredit ? "primary" : "gray"} variant="light">
                    {t("dashboard.call.status.planningCredit")}
                  </Badge>
                </div>

                <div className={classes.incomingActionsRow}>
                  <button className={classes.scheduleButton} onClick={handleOpenScheduleModal} type="button">
                    {t("dashboard.call.actions.scheduleCall")}
                  </button>

                  {selectedSearchResult.todoId ? (
                    <button
                      className={classes.scheduleButton}
                      onClick={() => setCancelAppointmentModalOpen(true)}
                      type="button"
                    >
                      {t("dashboard.call.actions.cancelAppointment")}
                    </button>
                  ) : null}
                </div>

                <div className={classes.outcomeSection}>
                  <Text className={classes.outcomeTitle}>{t("dashboard.call.incoming.outcomes.title")}</Text>

                  <div className={classes.outcomeOptions}>
                    {incomingOutcomeOptions.map((option) => (
                      <OutcomeOption
                        active={incomingOutcome === option.value}
                        key={option.value}
                        label={option.label}
                        onClick={() => setIncomingOutcome(option.value)}
                      />
                    ))}
                  </div>
                </div>

                <button
                  className={`${classes.saveButton} ${
                    incomingOutcome ? classes.saveButtonEnabled : classes.saveButtonDisabled
                  }`}
                  disabled={!incomingOutcome || isSaving}
                  onClick={handleIncomingOutcomeSave}
                  type="button"
                >
                  {t("dashboard.call.actions.save")}
                </button>
              </>
            ) : null}
          </>
        ) : (
          <>
            {widgetStatus === "loading" ? (
              <div className={classes.stateMessage}>
                <Loader size="sm" />
              </div>
            ) : null}

            {widgetStatus === "error" ? (
              <Text className={classes.stateMessage}>{t("dashboard.call.errorState")}</Text>
            ) : null}

            {widgetStatus === "empty" ? (
              <Text className={classes.stateMessage}>{t("dashboard.call.emptyState")}</Text>
            ) : null}

            {widgetStatus === "ready" && client ? (
              <>
                {client.todoId ? (
                  <Text className={classes.sourceTag}>{t("dashboard.call.todo.badge")}</Text>
                ) : null}

                <div className={classes.header}>
                  <div className={classes.headerMain}>
                    <div className={classes.clientHeadingRow}>
                      <Text className={classes.clientHeading}>{`${clientName}  ${phoneNumber}`}</Text>

                      <CopyButton value={phoneNumber} timeout={1200}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? t("dashboard.call.actions.copied") : t("dashboard.call.actions.copy")}
                            withArrow
                          >
                            <ActionIcon
                              aria-label={t("dashboard.call.actions.copy")}
                              className={classes.copyButton}
                              onClick={copy}
                              radius="xl"
                              variant="subtle"
                            >
                              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </div>

                    <Text className={classes.sourceTitle}>
                      {t(`source.options.${client.clientSource}`, { defaultValue: client.clientSource })}
                    </Text>
                  </div>

                  <RouterLink
                    aria-label={t("dashboard.call.actions.openClient")}
                    className={classes.profileLink}
                    to={`${RouteConstants.ROUTE_CLIENTS}/${client.clientId}`}
                  >
                    <IconUser size={20} stroke={1.9} />
                  </RouterLink>
                </div>

                <div className={classes.detailsCard}>
                  <div className={classes.detailsGrid}>
                    <TimeAvailability
                      from={formatTimeValue(client.workdayAvailabilityFrom)}
                      label={t("dashboard.call.fields.weekdayAvailability")}
                      t={t}
                      to={formatTimeValue(client.workdayAvailabilityTo)}
                    />

                    <TimeAvailability
                      from={client.saturdayAvailability ? formatTimeValue(client.saturdayAvailabilityFrom) : "–"}
                      label={t("dashboard.call.fields.saturdayAvailability")}
                      t={t}
                      to={client.saturdayAvailability ? formatTimeValue(client.saturdayAvailabilityTo) : "–"}
                    />

                    <div className={classes.infoBlock}>
                      <Text className={classes.infoTitle}>{t("dashboard.call.fields.language")}</Text>
                      <Text className={classes.languageValue}>{t(`user.language.options.${client.language}`)}</Text>
                    </div>
                  </div>

                  <div className={classes.statusBar}>
                    {(
                      [
                        ["hasCredit", t("dashboard.call.status.hasCredit")],
                        ["hasCreditCard", t("dashboard.call.status.hasCreditCard")],
                        ["hasLeasing", t("dashboard.call.status.hasLeasing")],
                        ["planningCredit", t("dashboard.call.status.planningCredit")],
                      ] as [ToggleableStatusField, string][]
                    ).map(([field, label]) => (
                      <div className={classes.statusItem} key={field}>
                        <Text className={classes.statusLabel}>{label}</Text>
                        <Switch
                          checked={!!client[field]}
                          onChange={() => handleToggleStatus(field)}
                          styles={{ track: { cursor: "pointer" }, thumb: { cursor: "pointer" } }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className={classes.divider} />

                {client.todoId ? (
                  <div className={classes.todoBlock}>
                    <div className={classes.todoHeaderRow}>
                      <div
                        className={`${classes.todoCheckbox} ${hasActionSelection ? classes.todoCheckboxChecked : ""}`}
                      >
                        {hasActionSelection ? <IconCheck size={17} stroke={2.5} /> : null}
                      </div>

                      <Text className={classes.todoTitle}>{client.todoTitle}</Text>
                    </div>

                    {client.todoDescription ? (
                      <Text className={classes.todoDescription}>{client.todoDescription}</Text>
                    ) : null}
                  </div>
                ) : null}

                <button className={classes.scriptsButton} onClick={() => setScriptsOpen(true)} type="button">
                  <IconBook size={20} stroke={1.9} />
                  <span>{t("callScripts.openButton")}</span>
                </button>

                <div className={classes.actionsRow}>
                  <ActionButton
                    active={selectedAction === "success"}
                    ariaLabel={t("dashboard.call.actions.success")}
                    onClick={() => handleActionChange("success")}
                    tone="success"
                  >
                    <IconPhoneCall size={22} stroke={2.1} />
                  </ActionButton>

                  <ActionButton
                    active={selectedAction === "failure"}
                    ariaLabel={t("dashboard.call.actions.failure")}
                    onClick={() => handleActionChange("failure")}
                    tone="failure"
                  >
                    <IconPhoneCall size={22} stroke={2.1} />
                  </ActionButton>

                  <ActionButton
                    active={selectedAction === "resolved"}
                    ariaLabel={t("dashboard.call.actions.resolvedWithoutCall")}
                    onClick={() => handleActionChange("resolved")}
                    tone="resolved"
                  >
                    {t("dashboard.call.actions.resolvedWithoutCall")}
                  </ActionButton>
                </div>

                {selectedAction === "success" ? (
                  <div className={classes.outcomeSection}>
                    <Text className={`${classes.outcomeTitle} ${classes.outcomeTitleSuccess}`}>
                      {t("dashboard.call.outcomes.success.title")}
                    </Text>

                    <div className={classes.outcomeOptions}>
                      {successOptions.map((option) => (
                        <OutcomeOption
                          active={selectedSuccessResult === option.value}
                          key={option.value}
                          label={option.label}
                          onClick={() => handleSuccessResultChange(option.value)}
                        />
                      ))}
                    </div>

                    {selectedSuccessResult === "scheduledFollowUp" ? (
                      <div className={classes.scheduledCallFields}>
                        <DateTimePicker
                          label={t("dashboard.call.outcomes.success.scheduledCall.dateLabel")}
                          onChange={setScheduledCallAt}
                          placeholder={t("dashboard.call.outcomes.success.scheduledCall.datePlaceholder")}
                          radius="md"
                          size="sm"
                          value={scheduledCallAt}
                        />

                        <TextInput
                          onChange={(event) => setScheduledCallNote(event.currentTarget.value)}
                          placeholder={t("dashboard.call.outcomes.success.scheduledCall.notePlaceholder")}
                          radius="md"
                          size="sm"
                          value={scheduledCallNote}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {selectedAction === "failure" ? (
                  <div className={classes.outcomeSection}>
                    <Text className={`${classes.outcomeTitle} ${classes.outcomeTitleFailure}`}>
                      {t("dashboard.call.outcomes.failure.title")}
                    </Text>

                    <div className={classes.outcomeOptions}>
                      {failureOptions.map((option) => (
                        <OutcomeOption
                          active={selectedFailureResult === option.value}
                          key={option.value}
                          label={option.label}
                          onClick={() => setSelectedFailureResult(option.value)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {selectedAction !== null && selectedAction !== "resolved" ? (
                  <button
                    className={`${classes.blacklistButton} ${blacklistReason ? classes.blacklistButtonActive : ""}`}
                    onClick={handleBlacklistButtonClick}
                    type="button"
                  >
                    <span className={classes.blacklistIcon}>
                      <IconX size={15} stroke={2.6} />
                    </span>
                    <span>{t("dashboard.call.actions.blacklistClient")}</span>
                  </button>
                ) : null}

                <button
                  className={`${classes.saveButton} ${canSave ? classes.saveButtonEnabled : classes.saveButtonDisabled}`}
                  disabled={!canSave || isSaving}
                  onClick={handleSave}
                  type="button"
                >
                  {t("dashboard.call.actions.save")}
                </button>
              </>
            ) : null}
          </>
        )}
      </div>

      <CallScriptsModal
        agentName={agentName}
        clientName={clientName}
        onClose={() => setScriptsOpen(false)}
        onScriptCompleted={(moduleId, endType) => setUsedScript({ moduleId, endType })}
        opened={scriptsOpen}
      />

      <Modal
        centered
        onClose={() => setBlacklistModalOpen(false)}
        opened={blacklistModalOpen}
        size="sm"
        styles={{ title: { fontSize: 18, fontWeight: 600 } }}
        title={t("dashboard.call.blacklistModal.title")}
      >
        <Textarea
          label={t("dashboard.call.blacklistModal.reasonLabel")}
          minRows={3}
          onChange={(event) => setBlacklistReasonDraft(event.currentTarget.value)}
          placeholder={t("dashboard.call.blacklistModal.reasonPlaceholder")}
          value={blacklistReasonDraft}
        />

        <Group justify="flex-end" mt="md">
          <Button color="gray" onClick={() => setBlacklistModalOpen(false)} radius="md" variant="outline">
            {t("action.cancel")}
          </Button>
          <Button color="red" disabled={!blacklistReasonDraft.trim()} onClick={handleBlacklistConfirm} radius="md">
            {t("dashboard.call.blacklistModal.confirm")}
          </Button>
        </Group>
      </Modal>

      <Modal
        centered
        onClose={() => setScheduleModalOpen(false)}
        opened={scheduleModalOpen}
        size="sm"
        styles={{ title: { fontSize: 18, fontWeight: 600 } }}
        title={t("dashboard.call.scheduleModal.title")}
      >
        <Select
          data={scheduleAgentOptions.map((agent) => ({ value: String(agent.id), label: agent.fullName }))}
          disabled={scheduleAgentOptionsLoading}
          label={t("dashboard.call.scheduleModal.agentLabel")}
          mb="md"
          onChange={setScheduleAgentId}
          placeholder={t("dashboard.call.scheduleModal.agentPlaceholder")}
          searchable
          value={scheduleAgentId}
        />

        <DateTimePicker
          label={t("dashboard.call.scheduleModal.dateLabel")}
          mb="md"
          onChange={setScheduleDueDate}
          placeholder={t("dashboard.call.scheduleModal.datePlaceholder")}
          value={scheduleDueDate}
        />

        <Textarea
          label={t("dashboard.call.scheduleModal.noteLabel")}
          minRows={3}
          onChange={(event) => setScheduleNote(event.currentTarget.value)}
          placeholder={t("dashboard.call.scheduleModal.notePlaceholder")}
          value={scheduleNote}
        />

        <Group justify="flex-end" mt="md">
          <Button color="gray" onClick={() => setScheduleModalOpen(false)} radius="md" variant="outline">
            {t("action.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={!scheduleAgentId || !scheduleDueDate}
            loading={isScheduling}
            onClick={handleScheduleConfirm}
            radius="md"
          >
            {t("dashboard.call.scheduleModal.confirm")}
          </Button>
        </Group>
      </Modal>

      <ConfirmationModal
        confirmLabel={t("dashboard.call.cancelAppointmentModal.confirm")}
        loading={isCancelingAppointment}
        message={t("dashboard.call.cancelAppointmentModal.message")}
        onClose={() => setCancelAppointmentModalOpen(false)}
        onConfirm={handleCancelAppointmentConfirm}
        opened={cancelAppointmentModalOpen}
        title={t("dashboard.call.cancelAppointmentModal.title")}
      />
    </div>
  );
};

export default CallWidgetSection;
